import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";

/**
 * Live countdown to the next Members Night.
 * Fetches server-computed next event (auto = next Friday 22:00 STHLM).
 * Editorial four-column layout: DAYS / HOURS / MINUTES / SECONDS.
 */
export default function EventCountdown() {
    const [ev, setEv] = useState(null);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        let alive = true;
        api.get("/public/next-event")
            .then((r) => { if (alive) setEv(r.data); })
            .catch(() => {});
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    if (!ev) return null;

    const target = new Date(ev.iso).getTime();
    let diff = Math.max(0, target - now);
    const day = 24 * 60 * 60 * 1000;
    const hour = 60 * 60 * 1000;
    const minute = 60 * 1000;
    const days = Math.floor(diff / day);      diff -= days * day;
    const hours = Math.floor(diff / hour);    diff -= hours * hour;
    const mins  = Math.floor(diff / minute);  diff -= mins * minute;
    const secs  = Math.floor(diff / 1000);

    const pad = (n) => String(n).padStart(2, "0");
    const parts = [
        { v: pad(days),  l: "Days" },
        { v: pad(hours), l: "Hours" },
        { v: pad(mins),  l: "Minutes" },
        { v: pad(secs),  l: "Seconds" },
    ];

    const evDate = new Date(ev.iso);
    const dateLine = evDate.toLocaleDateString("en-GB", {
        weekday: "long", day: "2-digit", month: "long",
        timeZone: "Europe/Stockholm",
    });
    const timeLine = evDate.toLocaleTimeString("en-GB", {
        hour: "2-digit", minute: "2-digit",
        timeZone: "Europe/Stockholm",
    });

    return (
        <section
            className="relative py-16 md:py-20 border-y border-gold/15 bg-black/30 overflow-hidden"
            data-testid="event-countdown"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                {/* Head row */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <div className="label-eyebrow flex items-center gap-3">
                            <span className="w-8 h-px bg-gold" />
                            Next Members Night
                        </div>
                        <h3 className="font-serif italic text-3xl md:text-4xl mt-3 text-ink-primary leading-none">
                            {ev.title}
                        </h3>
                    </div>
                    <div className="md:text-right">
                        <div className="text-ink-secondary text-sm tracking-[0.24em] uppercase" data-testid="event-date">
                            {dateLine}
                        </div>
                        <div className="font-serif text-2xl md:text-3xl text-gold mt-1">
                            {timeLine} · {ev.subtitle}
                        </div>
                    </div>
                </div>

                {/* Timer */}
                <div className="grid grid-cols-4 gap-px bg-gold/12" data-testid="countdown-timer">
                    {parts.map((p) => (
                        <div key={p.l} className="bg-bg-primary py-8 md:py-12 px-4 text-center">
                            <motion.div
                                key={p.v}
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                className="stat-num tabular-nums"
                            >
                                {p.v}
                            </motion.div>
                            <div className="label-eyebrow mt-3">{p.l}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
